// TechnicalDetailsPage.jsx
// Landing Page: Technical Details & Architecture
// Slug: /technical-details

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPages.css';

// SEO Meta Tags
const SEO = {
  title: 'Technical Details | VivaJour Architecture & Implementation',
  description: 'Detailed technical documentation of VivaJour\'s architecture: on-device processing, GPS clustering algorithms, photo metadata extraction, local storage, and privacy-first implementation. Built with React, Capacitor, and Firebase.',
  slug: '/technical-details'
};

export default function TechnicalDetailsPage() {
  useEffect(() => {
    document.title = SEO.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = SEO.description;
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-nav-logo">
            <span>VivaJour</span>
          </Link>
          <div className="landing-nav-links">
            <Link to="/privacy-first" className="landing-nav-link">Privacy</Link>
            <Link to="/current-trip" className="landing-nav-link">Current Trip</Link>
            <Link to="/albums" className="landing-nav-link">Albums</Link>
            <Link to="/how-it-works" className="landing-nav-link">How It Works</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <span className="landing-hero-badge">🔧 For Developers & Technical Users</span>
        <h1>Technical Architecture</h1>
        <p className="landing-hero-subtitle">
          Deep dive into VivaJour's implementation: on-device processing, privacy-first architecture, and the technology stack powering automatic travel album creation.
        </p>
      </section>

      {/* Architecture Overview */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container-narrow">
          <h2>Architecture Overview</h2>
          <p style={{ color: 'var(--landing-muted)', fontSize: '16px', lineHeight: '1.7', marginTop: '20px' }}>
            VivaJour is built as a hybrid mobile application using React, Capacitor, and native platform APIs. The core principle is privacy-first, on-device processing: all photo organization, metadata extraction, and album generation happens locally on the user's device.
          </p>
          <p style={{ color: 'var(--landing-text)', fontSize: '16px', lineHeight: '1.7', marginTop: '16px', fontWeight: 600 }}>
            Cloud services are used only for user-initiated sharing and preview functionality, never for processing or analysis.
          </p>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="landing-section">
        <div className="landing-container">
          <h2>Technology Stack</h2>
          
          <div className="landing-features-grid" style={{ marginTop: '40px' }}>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">⚛️</div>
              <h3>Frontend Framework</h3>
              <p><strong>React 18</strong> - Component-based UI with hooks for state management and side effects.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📱</div>
              <h3>Mobile Runtime</h3>
              <p><strong>Capacitor</strong> - Cross-platform native runtime providing access to device APIs on iOS and Android.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🔥</div>
              <h3>Cloud Services</h3>
              <p><strong>Firebase Storage & Hosting</strong> - Used exclusively for user-initiated sharing and preview functionality.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🎨</div>
              <h3>UI & Styling</h3>
              <p><strong>CSS Modules & Tailwind</strong> - Component-scoped styles with utility-first CSS for rapid development.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🧠</div>
              <h3>ML Processing</h3>
              <p><strong>Client-side ML</strong> - CLIP and YOLO models run on-device for photo curation and content analysis.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">💾</div>
              <h3>Local Storage</h3>
              <p><strong>IndexedDB</strong> - Browser-native database for persisting album structures and metadata locally.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Components */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2>Core Technical Components</h2>
          
          <div className="landing-steps" style={{ marginTop: '40px' }}>
            <div className="landing-step">
              <div className="landing-step-number">1</div>
              <div className="landing-step-content">
                <h3>Photo Access Layer</h3>
                <p><strong>iOS:</strong> PHPhotoLibrary API for accessing photo library with read-only permissions</p>
                <p><strong>Android:</strong> MediaStore API for camera roll access</p>
                <p className="landing-step-note">
                  Custom Capacitor plugin bypasses 100-photo system limit on Android, enabling access to complete photo library while maintaining privacy through read-only access.
                </p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">2</div>
              <div className="landing-step-content">
                <h3>Metadata Extraction</h3>
                <p><strong>EXIF Data:</strong> Extracts capture date, time, GPS coordinates, camera settings from photo headers</p>
                <p><strong>HEIC Processing:</strong> Native HEIC to JPEG conversion with GPS metadata preservation</p>
                <p className="landing-step-note">
                  All metadata extraction runs client-side using JavaScript. No photo data or metadata is transmitted to external servers during this process.
                </p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">3</div>
              <div className="landing-step-content">
                <h3>GPS Clustering Algorithm</h3>
                <p><strong>Proximity-based clustering:</strong> Groups GPS coordinates within configurable distance thresholds</p>
                <p><strong>Temporal weighting:</strong> Considers time gaps between photos to detect location transitions</p>
                <p><strong>Noise filtering:</strong> Removes GPS outliers and errors common in mobile photography</p>
                <p className="landing-step-note">
                  Clustering algorithms run in JavaScript on-device. No GPS coordinates are transmitted externally. Typical processing: 100-200 photos in under 2 seconds.
                </p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">4</div>
              <div className="landing-step-content">
                <h3>Geocoding & Place Resolution</h3>
                <p><strong>iOS:</strong> Apple Maps geocoding via CLGeocoder</p>
                <p><strong>Android:</strong> Google Geocoding via Geocoder API</p>
                <p className="landing-step-note">
                  Place name resolution uses device-level system services provided by the operating system. These services may cache data locally or make network requests — this is handled by iOS/Android, not by VivaJour. Our app does not directly transmit GPS coordinates to any VivaJour servers.
                </p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">5</div>
              <div className="landing-step-content">
                <h3>Photo Curation ML</h3>
                <p><strong>CLIP (Contrastive Language-Image Pre-training):</strong> Semantic understanding of photo content</p>
                <p><strong>YOLO (You Only Look Once):</strong> Object detection for identifying people, landmarks, scenes</p>
                <p><strong>Diversity scoring:</strong> Ensures varied photo selection representing different moments and perspectives</p>
                <p className="landing-step-note">
                  ML models run entirely on-device. Photos are never uploaded for AI analysis. Model inference happens in the browser using WebGL acceleration where available.
                </p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">6</div>
              <div className="landing-step-content">
                <h3>Album Generation & Templates</h3>
                <p><strong>Layout engine:</strong> Dynamic template system with responsive photo positioning</p>
                <p><strong>Map generation:</strong> Static map creation with trip routes and location markers</p>
                <p><strong>Stats calculation:</strong> Trip duration, distance traveled, locations visited computed from metadata</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">7</div>
              <div className="landing-step-content">
                <h3>Local Persistence</h3>
                <p><strong>IndexedDB:</strong> Stores album structures, metadata, user preferences</p>
                <p><strong>Capacity:</strong> Handles albums with 500+ photos without performance degradation</p>
                <p><strong>No cloud sync:</strong> All data persists locally; no automatic synchronization to external servers</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">8</div>
              <div className="landing-step-content">
                <h3>Export & Sharing</h3>
                <p><strong>PDF Generation:</strong> Client-side rendering using on-device PDF library</p>
                <p><strong>Firebase Storage:</strong> Photos uploaded only when user explicitly chooses to preview or share</p>
                <p><strong>User control:</strong> Share links can be deactivated, shared albums deleted at any time</p>
                <p className="landing-step-note">
                  This is the only step where photos leave the device — and only when explicitly initiated by the user.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy & Security */}
      <section className="landing-section">
        <div className="landing-container">
          <h2>Privacy & Security Implementation</h2>
          
          <div className="landing-features-grid" style={{ marginTop: '40px' }}>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🔒</div>
              <h3>On-Device Processing</h3>
              <p>All photo analysis, organization, and curation algorithms run locally in JavaScript. Photos are never uploaded for processing purposes.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🚫</div>
              <h3>No AI Training</h3>
              <p>User photos are never used to train ML models. Client-side inference uses pre-trained models downloaded with the app.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📍</div>
              <h3>GPS Privacy</h3>
              <p>GPS coordinates are processed entirely on-device. No location data is transmitted to VivaJour servers. OS-level geocoding may use system services.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🔐</div>
              <h3>Encrypted Sharing</h3>
              <p>When users share albums, photos are uploaded to Firebase Storage with HTTPS encryption. Access URLs are unguessable and controlled by the user.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">💾</div>
              <h3>Local Storage Only</h3>
              <p>Album data stored in IndexedDB remains on the user's device. No automatic cloud synchronization or backup to external servers.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">✋</div>
              <h3>User Control</h3>
              <p>Users maintain complete control over data sharing. Shared content can be deleted at any time, revoking access to previously shared links.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Performance Characteristics */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2>Performance Characteristics</h2>
          
          <div style={{ maxWidth: '800px', margin: '40px auto 0' }}>
            <div className="landing-feature-card">
              <h3>Processing Speed</h3>
              <ul style={{ listStyle: 'none', padding: 0, color: 'var(--landing-text)' }}>
                <li style={{ padding: '8px 0' }}>📸 <strong>Metadata extraction:</strong> ~50ms per photo</li>
                <li style={{ padding: '8px 0' }}>📍 <strong>GPS clustering:</strong> 100 photos in ~1 second</li>
                <li style={{ padding: '8px 0' }}>🗺️ <strong>Geocoding:</strong> Variable (OS-dependent, may require network)</li>
                <li style={{ padding: '8px 0' }}>🧠 <strong>ML curation:</strong> ~200ms per photo (device-dependent)</li>
                <li style={{ padding: '8px 0' }}>📄 <strong>Album generation:</strong> 50-photo album in ~2 seconds</li>
                <li style={{ padding: '8px 0' }}>💾 <strong>PDF export:</strong> ~5 seconds for 50-page album</li>
              </ul>
              <p className="landing-step-note" style={{ marginTop: '16px' }}>
                Performance varies based on device capabilities. Newer devices with faster processors and more RAM show significantly better performance.
              </p>
            </div>
            
            <div className="landing-feature-card" style={{ marginTop: '24px' }}>
              <h3>Offline Capabilities</h3>
              <p style={{ marginBottom: '16px' }}>The following operations work completely offline:</p>
              <ul style={{ color: 'var(--landing-text)', lineHeight: '1.8' }}>
                <li>Photo selection from camera roll</li>
                <li>Metadata extraction and GPS processing</li>
                <li>Location clustering and trip detection</li>
                <li>Album building and editing</li>
                <li>Caption and layout customization</li>
                <li>PDF generation</li>
              </ul>
              <p className="landing-step-note" style={{ marginTop: '16px' }}>
                Network required only for: Place name geocoding (optional), album preview/sharing (user-initiated)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Data Flow Diagram */}
      <section className="landing-section">
        <div className="landing-container-narrow">
          <h2>Data Flow</h2>
          
          <div style={{ background: 'var(--landing-surface)', padding: '32px', borderRadius: '12px', border: '2px solid var(--landing-border)', marginTop: '32px' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '20px' }}>On-Device Processing Flow</h3>
            <div style={{ fontFamily: 'monospace', fontSize: '14px', lineHeight: '2', color: 'var(--landing-text)' }}>
              Camera Roll → Photo Selection<br />
              &nbsp;&nbsp;↓<br />
              EXIF Reader → Metadata Extraction<br />
              &nbsp;&nbsp;↓<br />
              GPS Clustering → Location Groups<br />
              &nbsp;&nbsp;↓<br />
              Geocoder (OS) → Place Names<br />
              &nbsp;&nbsp;↓<br />
              ML Models → Photo Curation<br />
              &nbsp;&nbsp;↓<br />
              Layout Engine → Album Generation<br />
              &nbsp;&nbsp;↓<br />
              IndexedDB → Local Storage<br />
              &nbsp;&nbsp;↓<br />
              [User Choice] → PDF Export OR Firebase Upload
            </div>
            <p className="landing-step-note" style={{ marginTop: '20px' }}>
              Photos never leave the device except at the final step when the user explicitly chooses to preview or share.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container-narrow">
          <h2 style={{ textAlign: 'center' }}>Technical FAQ</h2>
          
          <div className="landing-faq">
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Can I inspect the code or is it open source?</h4>
              <p className="landing-faq-answer">VivaJour is currently closed source. We may consider open-sourcing specific components in the future, particularly privacy-critical code that could benefit from community security review.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">How do you handle HEIC photos from iPhones?</h4>
              <p className="landing-faq-answer">HEIC photos are processed on-device using native platform APIs. GPS metadata is extracted before any format conversion. When needed for web display, HEIC is converted to JPEG client-side with metadata preserved.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What ML models do you use for photo curation?</h4>
              <p className="landing-faq-answer">We use pre-trained CLIP models for semantic understanding and YOLO for object detection. These models run entirely in the browser using TensorFlow.js with WebGL acceleration. Models are downloaded with the app, not trained on user data.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">How accurate is the GPS clustering?</h4>
              <p className="landing-faq-answer">Accuracy depends on GPS precision in original photos. Modern smartphones typically provide 5-10 meter accuracy. Our clustering algorithm uses configurable distance thresholds (typically 100-500 meters) and temporal weighting to handle GPS drift and errors.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What happens to photos when I share an album?</h4>
              <p className="landing-faq-answer">Selected photos are uploaded to Firebase Storage with HTTPS encryption. Share URLs are generated with unguessable tokens. You control access and can delete shared albums at any time, which removes photos from storage and breaks the share link.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Can the app work completely offline?</h4>
              <p className="landing-faq-answer">Yes, for all core functionality: photo selection, organization, editing, and PDF generation. The only network requirements are: (1) OS-level geocoding for place names (optional), and (2) user-initiated preview/sharing.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What are the system requirements?</h4>
              <p className="landing-faq-answer">iOS 14+ or Android 8+. Recommended: iOS 15+ or Android 10+ for best performance. ML processing benefits significantly from newer devices with more powerful processors.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Main Pages */}
      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-internal-links">
            <Link to="/how-it-works" className="landing-internal-link">
              ⚙️ How It Works — User-friendly explanation
            </Link>
            <Link to="/privacy-first" className="landing-internal-link">
              🔒 Privacy-First — Our privacy philosophy
            </Link>
            <Link to="/albums" className="landing-internal-link">
              📚 Albums — See what you can create
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/privacy-first" className="landing-footer-link">Privacy</Link>
          <Link to="/current-trip" className="landing-footer-link">Current Trip</Link>
          <Link to="/albums" className="landing-footer-link">Albums</Link>
          <Link to="/how-it-works" className="landing-footer-link">How It Works</Link>
          <Link to="/templates" className="landing-footer-link">Templates</Link>
        </div>
        <p className="landing-footer-meta">
          VivaJour Labs LLC • Built in Wisconsin
        </p>
        <p className="landing-footer-meta" style={{ marginTop: '8px' }}>
          <Link to="/privacy-policy" className="landing-footer-link">Privacy Policy</Link>
          {' • '}
          <Link to="/terms" className="landing-footer-link">Terms of Service</Link>
          {' • '}
          <Link to="/contact" className="landing-footer-link">Contact</Link>
        </p>
        <p className="landing-footer-tagline">
          © 2025 VivaJour. Your photos stay on your device. Your memories stay yours.
        </p>
      </footer>
    </div>
  );
}
